import { player, type PlayerStatusUpdate } from './player/player';
import { initDB, getDeviceRegistration, setDeviceRegistration, clearAllData } from './utils/storage';
import { analytics } from './player/analytics';
import { api } from './utils/api';
import QRCode from 'qrcode';

// DOM Elements
const pairingScreen = document.getElementById('pairing')!;
const pairingCode = document.getElementById('pairing-code')!;
const pairingStatus = document.getElementById('pairing-status');
const qrCanvas = document.getElementById('qr-canvas') as HTMLCanvasElement;
const connectedOverlay = document.getElementById('connected')!;
const loadingScreen = document.getElementById('loading')!;
const errorScreen = document.getElementById('error')!;
const errorMessage = document.getElementById('error-message')!;
const retryButton = document.getElementById('retry-btn')!;
const noContentScreen = document.getElementById('no-content')!;
const noContentDeviceId = document.getElementById('no-content-device-id')!;
const statusBar = document.getElementById('status-bar')!;
const statusDot = document.getElementById('status-dot')!;
const statusText = document.getElementById('status-text')!;
const statusInfo = document.getElementById('status-info')!;

// Current pairing code for polling
let currentPairingCode: string | null = null;

// Generate QR code using qrcode library
async function generateQRCode(code: string): Promise<void> {
  const pairingUrl = `govista://pair?code=${code}`;

  try {
    await QRCode.toCanvas(qrCanvas, pairingUrl, {
      width: 160,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#ffffff'
      },
      errorCorrectionLevel: 'M'
    });
  } catch (error) {
    console.error('Failed to generate QR code:', error);
    const ctx = qrCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 160, 160);
      ctx.fillStyle = '#000000';
      ctx.font = '14px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(code, 80, 85);
    }
  }
}

async function showPairingScreen(): Promise<void> {
  pairingScreen.classList.add('visible');
  loadingScreen.style.display = 'none';
  errorScreen.classList.remove('visible');

  // Show loading state while getting code from server
  pairingCode.textContent = '--------';
  if (pairingStatus) {
    pairingStatus.textContent = 'Connecting to server...';
  }

  try {
    // Get pairing code from API
    const response = await api.startPairing();

    if (response.success && response.data) {
      currentPairingCode = response.data.pairingCode;
      pairingCode.textContent = currentPairingCode;
      await generateQRCode(currentPairingCode);

      if (pairingStatus) {
        pairingStatus.textContent = 'Waiting for pairing...';
      }

      console.log('Got pairing code from server:', currentPairingCode);
    } else {
      console.error('Failed to get pairing code:', response.error);
      pairingCode.textContent = 'ERROR';
      if (pairingStatus) {
        pairingStatus.textContent = response.error?.message || 'Failed to connect to server';
      }
    }
  } catch (error) {
    console.error('Failed to start pairing:', error);
    pairingCode.textContent = 'ERROR';
    if (pairingStatus) {
      pairingStatus.textContent = 'Cannot reach server. Check network.';
    }
  }
}

function hidePairingScreen(): void {
  pairingScreen.classList.remove('visible');
}

function showConnectedAnimation(): Promise<void> {
  return new Promise((resolve) => {
    connectedOverlay.classList.add('visible');
    setTimeout(() => {
      connectedOverlay.classList.remove('visible');
      resolve();
    }, 2500);
  });
}

function showLoading(): void {
  loadingScreen.style.display = 'flex';
}

function hideLoading(): void {
  loadingScreen.style.display = 'none';
}

function showError(message: string): void {
  hideLoading();
  hidePairingScreen();
  hideNoContent();
  errorMessage.textContent = message;
  errorScreen.classList.add('visible');
}

function hideError(): void {
  errorScreen.classList.remove('visible');
}

function showNoContent(deviceId?: string): void {
  console.log('>>> showNoContent called');
  console.log('>>> loadingScreen before hide:', loadingScreen, loadingScreen?.style?.display);
  hideLoading();
  console.log('>>> loadingScreen after hide:', loadingScreen?.style?.display);
  hidePairingScreen();
  hideError();
  if (deviceId) {
    noContentDeviceId.textContent = deviceId;
  }
  console.log('>>> noContentScreen before visible:', noContentScreen, noContentScreen?.classList?.toString());
  noContentScreen.classList.add('visible');
  console.log('>>> noContentScreen after visible:', noContentScreen?.classList?.toString());
}

function hideNoContent(): void {
  noContentScreen.classList.remove('visible');
}

function updateStatusBar(update: PlayerStatusUpdate): void {
  const isOnline = update.status === 'playing' || update.status === 'no_content';

  statusDot.className = `status-dot ${isOnline ? 'online' : update.status === 'offline' ? 'offline' : ''}`;

  switch (update.status) {
    case 'loading':
      statusText.textContent = 'Loading...';
      break;
    case 'playing':
      statusText.textContent = 'Playing';
      break;
    case 'paused':
      statusText.textContent = 'Paused';
      break;
    case 'offline':
      statusText.textContent = 'Offline';
      break;
    case 'error':
      statusText.textContent = 'Error';
      break;
    case 'no_content':
      statusText.textContent = 'Waiting for Content';
      break;
  }

  if (update.item) {
    statusInfo.textContent = update.item.assetId;
  }
}

// Store device ID for use in no content screen
let currentDeviceId: string | null = null;

function handleStatusUpdate(update: PlayerStatusUpdate): void {
  console.log('Status update:', update);
  updateStatusBar(update);

  switch (update.status) {
    case 'loading':
      break;
    case 'playing':
      hideLoading();
      hideError();
      hideNoContent();
      hidePairingScreen();
      break;
    case 'error':
      if (update.message?.includes('not registered') || update.message?.includes('pairing')) {
        showPairingScreen();
      } else {
        showError(update.message || 'An error occurred');
      }
      break;
    case 'offline':
      hideLoading();
      statusBar.classList.add('visible');
      break;
    case 'no_content':
      console.log('>>> Handling no_content case, calling showNoContent');
      showNoContent(currentDeviceId || undefined);
      break;
  }
}

// Initialize the player
// 3B — Detect that the PWA service worker just landed a new bundle by
// comparing the current build-time-injected version to what we saw last
// startup. When they differ, the device has been updated since its last
// boot (either via SW auto-update or explicit COMMAND_UPDATE_CHECK). We
// flash a brief on-screen banner for ops visibility — handy when looking
// at a wall of screens to see updates rolling through.
function showBundleUpdateBannerIfChanged(): void {
  const STORAGE_KEY = 'govista_last_bundle_version';
  const current = typeof __BUNDLE_VERSION__ !== 'undefined' ? __BUNDLE_VERSION__ : null;
  if (!current) return;

  let previous: string | null = null;
  try {
    previous = localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable — skip silently
    return;
  }

  // Always stamp current so next boot has the new baseline.
  try {
    localStorage.setItem(STORAGE_KEY, current);
  } catch {
    // ignore
  }

  // First boot ever (no previous stored) — don't flash, it's not really
  // an "update".
  if (!previous || previous === current) return;

  console.log(`[Player] Bundle updated: ${previous} → ${current}`);

  // Minimal DOM-based banner. Auto-dismisses after 4s.
  const banner = document.createElement('div');
  banner.style.cssText = [
    'position: fixed',
    'top: 12px',
    'left: 50%',
    'transform: translateX(-50%)',
    'z-index: 999999',
    'padding: 8px 16px',
    'background: rgba(16, 185, 129, 0.9)',
    'color: #fff',
    'font-family: -apple-system, sans-serif',
    'font-size: 12px',
    'font-weight: 600',
    'border-radius: 6px',
    'box-shadow: 0 4px 12px rgba(0,0,0,0.25)',
    'pointer-events: none',
    'opacity: 0',
    'transition: opacity 300ms ease',
  ].join(';');
  banner.textContent = `✓ Updated → ${current}`;
  document.body.appendChild(banner);
  requestAnimationFrame(() => {
    banner.style.opacity = '1';
  });
  setTimeout(() => {
    banner.style.opacity = '0';
    setTimeout(() => banner.remove(), 400);
  }, 4000);
}

async function init(): Promise<void> {
  console.log('GoVista Web Player starting...');
  showBundleUpdateBannerIfChanged();

  try {
    await initDB();

    // Check if device is already registered
    const registration = await getDeviceRegistration();

    if (!registration) {
      console.log('Device not registered, showing pairing screen');
      showPairingScreen();
      pollForPairing();
      return;
    }

    console.log('Device registered:', registration.deviceId);
    currentDeviceId = registration.deviceId;
    api.setRegistration(registration);
    showLoading();

    const success = await player.initialize(handleStatusUpdate);

    if (success) {
      await showConnectedAnimation();
      await player.start();
    } else {
      // Player failed to initialize - might be invalid credentials
      // Check if we should clear registration and re-pair
      console.log('Player initialization failed, checking if re-pairing needed...');
      showError('Failed to connect to server. Click retry to re-pair the device.');
    }
  } catch (error) {
    console.error('Failed to start player:', error);
    const errorMsg = error instanceof Error ? error.message : 'Failed to start player';

    // Check for auth errors (401) - clear registration and re-pair
    if (errorMsg.includes('401') || errorMsg.includes('Unauthorized') ||
        errorMsg.includes('not registered') || errorMsg.includes('pairing')) {
      console.log('Auth error detected, clearing registration and re-pairing...');
      await clearAllData();
      showPairingScreen();
      pollForPairing();
    } else {
      showError(errorMsg);
    }
  }
}

// Poll for pairing completion
let pairingPollInterval: ReturnType<typeof setInterval> | null = null;
let isFinishingPairing = false; // Prevent race condition

async function pollForPairing(): Promise<void> {
  if (pairingPollInterval) {
    clearInterval(pairingPollInterval);
  }

  isFinishingPairing = false;

  pairingPollInterval = setInterval(async () => {
    // Check if we have a pairing code to poll for
    if (!currentPairingCode) {
      console.log('No pairing code yet, waiting...');
      return;
    }

    // Prevent race condition - don't poll if already finishing
    if (isFinishingPairing) {
      console.log('Already finishing pairing, skipping poll...');
      return;
    }

    try {
      // Check pairing status with API
      const statusResponse = await api.checkPairingStatus(currentPairingCode);

      if (!statusResponse.success) {
        // Pairing code expired or not found
        if (statusResponse.error?.code === 'PAIRING_NOT_FOUND') {
          console.log('Pairing code expired, getting new one...');
          clearInterval(pairingPollInterval!);
          pairingPollInterval = null;
          await showPairingScreen();
          pollForPairing();
        }
        return;
      }

      if (statusResponse.data?.status === 'APPROVED') {
        // Stop polling immediately to prevent race condition
        isFinishingPairing = true;
        clearInterval(pairingPollInterval!);
        pairingPollInterval = null;

        console.log('Pairing approved by admin, completing...');

        if (pairingStatus) {
          pairingStatus.textContent = 'Approved! Completing setup...';
        }

        // Finish pairing and get credentials
        const finishResponse = await api.finishPairing(currentPairingCode);

        if (finishResponse.success && finishResponse.data) {
          const registration = {
            deviceId: finishResponse.data.deviceId,
            deviceSecret: finishResponse.data.deviceSecret,
            brandId: finishResponse.data.brandId,
            storeId: finishResponse.data.storeId,
            zoneId: finishResponse.data.zoneId,
          };

          // Save registration
          await setDeviceRegistration(registration);
          api.setRegistration(registration);
          currentDeviceId = registration.deviceId;

          console.log('Device paired successfully!', registration.deviceId);
          currentPairingCode = null;

          hidePairingScreen();
          await showConnectedAnimation();
          showLoading();

          const success = await player.initialize(handleStatusUpdate);
          if (success) {
            await player.start();
          } else {
            // Player initialization failed but device is registered
            // Don't show pairing screen again - show error instead
            showError('Failed to initialize player. Please refresh the page.');
          }
        } else {
          console.error('Failed to complete pairing:', finishResponse.error);
          if (pairingStatus) {
            pairingStatus.textContent = finishResponse.error?.message || 'Failed to complete pairing';
          }
          // Reset flag and restart polling on failure
          isFinishingPairing = false;
          pollForPairing();
        }
      } else {
        console.log('Still waiting for admin approval...');
      }
    } catch (error) {
      console.error('Error checking pairing status:', error);
      // Reset flag on error so polling can continue
      if (isFinishingPairing) {
        isFinishingPairing = false;
      }
    }
  }, 3000);
}

// Event listeners
retryButton.addEventListener('click', async () => {
  hideError();
  // Clear all data and start fresh pairing
  await clearAllData();
  showPairingScreen();
  pollForPairing();
});

// Handle visibility change (screen on/off)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    console.log('Page hidden');
  } else {
    console.log('Page visible');
  }
});

// Handle online/offline events
window.addEventListener('online', () => {
  console.log('Network online');
  updateStatusBar({ status: 'playing' });
});

window.addEventListener('offline', () => {
  console.log('Network offline');
  updateStatusBar({ status: 'offline', message: 'No network connection' });
});

// Handle before unload
window.addEventListener('beforeunload', () => {
  analytics.forceFlush();
  if (pairingPollInterval) {
    clearInterval(pairingPollInterval);
  }
});

// Prevent screen sleep with Wake Lock API
async function requestWakeLock(): Promise<void> {
  if ('wakeLock' in navigator) {
    try {
      const wakeLock = await navigator.wakeLock.request('screen');
      console.log('Wake lock acquired');

      wakeLock.addEventListener('release', () => {
        console.log('Wake lock released');
      });

      document.addEventListener('visibilitychange', async () => {
        if (!document.hidden) {
          try {
            await navigator.wakeLock.request('screen');
          } catch {
            console.log('Failed to re-acquire wake lock');
          }
        }
      });
    } catch (err) {
      console.log('Wake lock not supported:', err);
    }
  }
}

// Request fullscreen
function requestFullscreen(): void {
  const elem = document.documentElement;

  if (elem.requestFullscreen) {
    elem.requestFullscreen().catch(() => {
      console.log('Fullscreen request denied');
    });
  }
}

// Fullscreen hint visibility — fades when fullscreen is active or after idle.
// Browsers refuse programmatic requestFullscreen() outside a user gesture,
// so the hint nudges the operator to click once on initial setup.
const fullscreenHint = document.getElementById('fullscreen-hint');
function setHintVisible(visible: boolean): void {
  if (!fullscreenHint) return;
  fullscreenHint.style.opacity = visible ? '0.55' : '0';
}

// Try fullscreen on the FIRST interaction the page gets (click, key, touch).
// `{ once: true }` so we don't keep firing on every click.
const tryFullscreenOnFirstGesture = () => {
  if (!document.fullscreenElement) {
    requestFullscreen();
  }
};
document.addEventListener('click', tryFullscreenOnFirstGesture, { once: true });
document.addEventListener('keydown', tryFullscreenOnFirstGesture, { once: true });
document.addEventListener('touchstart', tryFullscreenOnFirstGesture, { once: true, passive: true });

// Hide the hint when fullscreen is entered, show when exited.
document.addEventListener('fullscreenchange', () => {
  setHintVisible(!document.fullscreenElement);
});

// Fade the hint after 8s even if fullscreen never activates — for headless
// signage devices where there's no operator to click.
setTimeout(() => {
  if (!document.fullscreenElement) setHintVisible(false);
}, 8000);

// Double-click to toggle fullscreen
document.addEventListener('dblclick', () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    requestFullscreen();
  }
});

// Toggle status bar with single click
let statusBarTimeout: ReturnType<typeof setTimeout> | null = null;
document.addEventListener('click', () => {
  statusBar.classList.add('visible');

  if (statusBarTimeout) {
    clearTimeout(statusBarTimeout);
  }

  statusBarTimeout = setTimeout(() => {
    statusBar.classList.remove('visible');
  }, 3000);
});

// Keyboard shortcuts
document.addEventListener('keydown', (e) => {
  switch (e.key) {
    case 'f':
    case 'F':
      requestFullscreen();
      break;
    case 'Escape':
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
      break;
  }
});

// Start the player
requestWakeLock();
init();
